import React, { useContext, useEffect, useState } from 'react';
import moment from 'moment';
import { IoIosCheckbox } from 'react-icons/io';

import { ProductContext } from '../../../context/productContext';

import FarmItems from './FarmItems';
import FormSelect from '../../form/select';
import Location from './Configure/Location';
import Crop from './Configure/Crop';
import Manager from './Configure/Manager';

import Umbrella from '../../../images/insurance.svg';

const FarmConfigure = ({
  setProduct,
  product,
  selectedPackage,
  selectedAcreage,
  setSelectedAcreage,
  setCost,
  setSelectedCycle,
  handleChange,
  setLoading,
  setUnexpectedError,
  session,
  setSession
}) => {
  const { getProduct } = useContext(ProductContext);
  const [errorMessage, setErrorMessage] = useState(false);

  function discountedPrice(acreage, price) {
    if (acreage >= 10) {
      return acreage * price - acreage * parseFloat(0.07 * price).toFixed(2);
    } else {
      return acreage * price;
    }
  }

  useEffect(() => {
    let mounted = true;
    if (Object.keys(product).length) {
      if (mounted) {
        setCost(product.pricePerAcre);
      }
    } else {
      (async () => {
        setLoading(true);
        try {
          const response = await getProduct({ package: selectedPackage.level });
          if (mounted) {
            if (response.code === 200) {
              if (response.data) {
                setProduct(response.data);
              } else setErrorMessage(response.message);
            } else setErrorMessage(response.data.message);
          }
        } catch (error) {
          setUnexpectedError('Unexpected Network Error!');
        }
        setLoading(false);
      })();
    }
    return () => (mounted = false);
  }, [
    getProduct,
    product,
    selectedPackage.level,
    setCost,
    setProduct,
    setLoading,
    setUnexpectedError
  ]);

  useEffect(() => {
    if (selectedAcreage && product.pricePerAcre) {
      const cost = discountedPrice(parseInt(selectedAcreage), parseInt(product.pricePerAcre));
      setCost(cost);
    }
  }, [product.pricePerAcre, selectedAcreage, setCost]);

  const cycles = [1, 2, 3, 4, 5];

  return (
    <div>
      {Object.keys(product).length ? (
        <FarmItems
          title="Choose the number of acres you want to farm and farm package"
          subtitle="Here’s your assigned crop to start farming "
          small
        >
          <div className="w-full md:w-1/2 md:pr-2 mb-10 md:mb-0 text-left">
            <div className="rounded bg-white shadow-cfw border border-cf-300 px-4 py-5">
              <div className="flex flex-wrap">
                <div className="flex">
                  <div className="w-1/4 mr-6">
                    <p className="w-full">&nbsp;</p>
                  </div>
                  <div className="w-full md:w-3/4">
                    <div className="flex">
                      <div className="w-full md:w-1/2 pr-1">
                        <div className="border-red-500 border rounded p-1 bg-red-100 text-red-500 w-32">
                          <p className="text-xx text-center">
                            <span>Farm Starts:</span> {moment(product.startDate).format('L')}
                          </p>
                        </div>
                      </div>
                      <div className="w-full md:w-1/2 md:pl-1 md:ml-4">
                        <div className="border-cf-300 border rounded p-1 bg-cf-100 text-cf-300 w-32">
                          <p className="text-xx text-center">
                            <span>No of acres left:</span> {product.acreage}
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="mt-4 text-tiny">
                  {product.crop && <Crop product={product} />}
                  {product.location && <Location location={product.location} />}
                  {product.manager && <Manager manager={product.manager} />}
                </div>
              </div>
            </div>
          </div>
          <div className="w-full md:w-1/2 md:pl-2 text-left">
            <div className="rounded bg-white shadow-cfw border border-cf-300 p-4">
              <div className="flex flex-wrap items-center">
                <div className="w-full md:w-1/2 md:pr-2">
                  <div className="w-full mb-2">
                    <FormSelect
                      name="acreage"
                      title="How many acres?"
                      value={selectedAcreage}
                      change={e => {
                        handleChange(
                          e,
                          discountedPrice(parseInt(e.target.value), parseInt(product.pricePerAcre))
                        );
                      }}
                    >
                      {[...Array(product.acreage).keys()].map((item, index) => (
                        <option key={index}>{item + 1}</option>
                      ))}
                    </FormSelect>
                  </div>
                </div>

                <div className="w-full md:w-1/2 md:pl-2">
                  <div className="rounded bg-cf-200 text-cf-300 p-3 text-center border border-cf-300 font-bold">
                    <p>
                      ${discountedPrice(parseInt(selectedAcreage), parseInt(product.pricePerAcre))}
                    </p>
                  </div>
                </div>
                {parseInt(selectedAcreage) >= 10 && (
                  <p className="text-sm text-cf-300">You have a discount of 7%</p>
                )}
              </div>

              <div className="rounded bg-cf-200 p-2 my-3">
                <div className="flex justify-between items-center">
                  <img src={Umbrella} alt="Umbrella" />
                  <div>
                    <p className="font-header">Farm Insurance</p>
                    <p className="text-xs leading-none">
                      Protects your farm from natural perils like floods, fire, pests, strong winds
                      and droughts with insurance for your farm.
                    </p>
                  </div>

                  <div className="text-2xl text-cf-300">
                    <IoIosCheckbox />
                  </div>
                </div>
              </div>
              <div className="flex items-center my-4">
                <div className="w-full md:w-1/2 md:pr-2">
                  <div className="w-full mb-2">
                    <FormSelect name="cycle" title="Choose your cycle" change={handleChange}>
                      {cycles.map((item, i) => (
                        <option key={i}>{item}</option>
                      ))}
                    </FormSelect>
                  </div>
                </div>

                <div className="w-full md:w-1/2 md:pl-2">
                  <p className="font-bold">What's a cycle?</p>
                  <p className="leading-none text-xs">
                    A cycle allows user to automatically farm continuously the same farm at the same
                    cost with yield profits being paid at the end of every production cycle.
                  </p>
                </div>
              </div>
              <div className="border-b pb-4 border-cf-300">
                <p className="text-base font-bold">This plan covers</p>

                <ul className="text-left text-xs">
                  <li>Customer Support</li>
                  <li>Farm Manager Support &amp; Updates</li>
                  <li>Live Sensitive Data</li>
                  <li>Live Support</li>
                  <li>Schedule Farm Visits</li>
                  <li>Farm Manage Q/A</li>
                  <li>Live Video Feed</li>
                </ul>
              </div>
            </div>
          </div>
        </FarmItems>
      ) : (
        <div>{errorMessage}</div>
      )}
    </div>
  );
};

export default FarmConfigure;
